import React from 'react';
import { getAskingPrice, mapValueToMoneyFormat } from 'utils';
import ResolveImage from 'components/ResolveImage';
import { getImgUrl, RESIZED_WIDTH } from 'components/ResolveImage/utils';
import { ASKING_PRICE_SELECT, DealRoom } from 'models/DealRoom';
import { Listing } from 'models/Listing';
import { CommonTable } from './data';
import useShowFieldVisibleInfo from 'hooks/useShowFieldVisibleInfo';

export type CellProps = {
  dealRoom?: DealRoom;
  listing?: Listing;
};

export const ListingTitleCell: React.FC<CellProps> = ({
  dealRoom,
  listing,
}) => {
  const { portfolioName } = dealRoom || {};
  const imageSrc = listing.gallery[0]?.url;

  return (
    <div className="pdt-name-cell">
      <ResolveImage
        alt="Listing"
        src={getImgUrl(imageSrc, RESIZED_WIDTH[320])}
      />
      <span>{portfolioName}</span>
    </div>
  );
};

export const IndustryCellComponent: React.FC<CellProps> = ({ dealRoom }) => {
  const { industry } = dealRoom || {};

  return <div className="pdt-industry-cell">{industry}</div>;
};

export const PriceCellComponent: React.FC<CellProps> = ({ listing }) => {
  const askingPrice = getAskingPrice(listing);

  const getShowFieldVisibleInfo = useShowFieldVisibleInfo();

  if (askingPrice) return <div className="pdt-manage-cell">{askingPrice}</div>;

  const {
    askingPriceSelect,
    amountRequiredVisibleInfo,
    amountToRequiredVisibleInfo,
    amountFromRequiredVisibleInfo,
  } = listing || {};

  if (askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE) {
    const { isHidden, reasonToHideContent } = getShowFieldVisibleInfo(
      amountRequiredVisibleInfo,
    );

    if (isHidden)
      return <div className="pdt-manage-cell">{reasonToHideContent}</div>;
  }

  const {
    isHidden: isHiddenFrom,
    reasonToHideContent: reasonToHideContentFrom,
  } = getShowFieldVisibleInfo(amountFromRequiredVisibleInfo);
  const { isHidden: isHiddenTo, reasonToHideContent: reasonToHideContentTo } =
    getShowFieldVisibleInfo(amountToRequiredVisibleInfo);

  if (isHiddenFrom || isHiddenTo)
    return (
      <div className="pdt-manage-cell">
        {reasonToHideContentFrom || reasonToHideContentTo}
      </div>
    );

  return null;
};

export const EbitdaCellComponent: React.FC<CellProps> = ({ listing }) => {
  const { EBITDA, EBITDARequiredVisibleInfo } = listing;

  const { isHidden, reasonToHideContent } = useShowFieldVisibleInfo()(
    EBITDARequiredVisibleInfo,
  );

  if (EBITDA)
    return (
      <div className="pdt-ebidta-cell">{mapValueToMoneyFormat(EBITDA)}</div>
    );

  if (isHidden)
    return <div className="pdt-ebidta-cell">{reasonToHideContent}</div>;

  return null;
};

export const StateCellComponent: React.FC<CellProps> = ({ dealRoom }) => {
  const { location } = dealRoom;

  if (!location) return null;

  return <div className="pdt-state-cell">{location}</div>;
};

export const COMMON_BODY_CONTENT_GENERATE: Record<
  keyof CommonTable,
  (cellProps: CellProps) => React.ReactNode
> = {
  listing: (cellProps: CellProps) => <ListingTitleCell {...cellProps} />,
  industry: (cellProps: CellProps) => <IndustryCellComponent {...cellProps} />,
  price: (cellProps: CellProps) => <PriceCellComponent {...cellProps} />,
  ebitda: (cellProps: CellProps) => <EbitdaCellComponent {...cellProps} />,
  state: (cellProps: CellProps) => <StateCellComponent {...cellProps} />,
};
